import { useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import classNames from 'classnames';

import { PaymentProcessEnum, PaymentStatusEnum } from '../../../lib/enums';
import lang from '../../../lib/language';
import { Payment } from '../../../lib/models/crm/types';
import { RootState } from '../../../lib/store';
import { PaymentCall } from '../../../lib/store/payment/types';
import { isCallStatusPending } from '../../../lib/util/error-handling/StatusByCallHelpers';
import { usePayments } from '../../../lib/util/hooks/payouts/PaymentHook';
import { TextHelpers } from '../../../lib/util/TextHelpers';

import DateRangeEmpty from '../../../assets/img/icon-no-date-range.svg';
import NoDataIcon from '../../../assets/img/no-data-package.svg';
import { BaseModal } from '../../../components';
import { useIsMobile } from '../../../hooks';
import Button from '../../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../../shared_components/Button/Button.types';
import ClickableDiv from '../../../shared_components/ClickableDiv/ClickableDiv';
import DateRangePickerWrapper from '../../../shared_components/DesktopCalendar/DateRangePickerWrapper';
import { UI_ROUTES } from '../../../util/routes';
import { XL_BREAKPOINT_WIDTH } from '../constants';
import Modal from '../modal/paymentsModal/BasicModal';

import TransactionItem from './TransactionItem';

import styles from './Transactions.module.scss';

type Props = {
  isUserVerified: boolean;
  browserWidthSize: number;
} & ReduxProps;

const ALLOWED_STATUSES = new Set<number>([
  PaymentStatusEnum.ForApproval,
  PaymentStatusEnum.OnHold,
  PaymentStatusEnum.Completed,
]);

const isPayout = (p: Payment) =>
  p?.is_cash_account === true
  && p?.payment_process === PaymentProcessEnum.Withdrawal
  && ALLOWED_STATUSES.has(p?.payment_status);

const skeletonIds = ['loader-1', 'loader-2', 'loader-3', 'loader-4'];

const Transactions = (props: Props) => {
  const { allTransactions, isUserVerified, browserWidthSize, isAllTransactionsLoading } = props;
  const { isMobile } = useIsMobile();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [infoModal, setInfoModal] = useState({ values: {} });

  const {
    number: pageNumber,
    setDateRange,
    setPage,
    filteredContent,
  } = usePayments(isUserVerified);

  const maxRusultMob = 10;
  const navigate = useNavigate();

  const { oldestPayoutISO, hasPayoutsAllTime } = useMemo(() => {
    const payoutsAll = (allTransactions || []).filter(isPayout);
    if (!payoutsAll.length) {
      return { oldestPayoutISO: new Date().toISOString(), hasPayoutsAllTime: false };
    }
    const oldest = payoutsAll
      .slice()
      .sort(
        (a, b) => new Date(a.create_date).getTime() - new Date(b.create_date).getTime(),
      )[0].create_date;
    return { oldestPayoutISO: oldest, hasPayoutsAllTime: true };
  }, [allTransactions]);

  const payoutsFromHook = useMemo(
    () =>
      (filteredContent || [])
        .filter((item) => isPayout(item.orderOrPayment as Payment))
        .sort(
          (a, b) =>
            new Date((b.orderOrPayment as Payment).create_date).getTime()
            - new Date((a.orderOrPayment as Payment).create_date).getTime(),
        ),
    [filteredContent],
  );
  // eslint-disable-next-line no-console
  console.log(allTransactions);

  const hasPayoutsFiltered = payoutsFromHook.length > 0;
  const isLoading = isAllTransactionsLoading;
  const hasMorePayouts = (allTransactions || []).filter(isPayout).length > payoutsFromHook.length;

  const showNeverMadePayout = !isLoading && !hasPayoutsAllTime;
  const showDateRangeEmpty = !isLoading && hasPayoutsAllTime && !hasPayoutsFiltered;

  const handleDateChange = (value) => setDateRange(value);
  const handleModalTransfer = () => setShowModal(!showModal);

  const handlePaymentClick = (payment: Payment) => {
    const values = {
      hasLinkedAccounts: true,
      amount: payment.amount,
      create_date: payment.create_date,
      payment_method: payment.payment_method,
      category: TextHelpers.getPaymentProcessName(payment.payment_process),
      adjustmentCategory: payment.category,
      bankName: payment.sender_bank_name,
      paymentReference: payment.payment_reference,
      paymentStatus: payment.payment_status,
      paymentProcess: payment.payment_process,
      description: payment.description,
      payment,
    };

    setInfoModal({ ...infoModal, values });
    handleModalTransfer();
  };

  const handleButtonLoadMore = () => setPage(pageNumber + 1);

  return (
    <div className={styles.wrapper}>
      <div className={styles.wrapperTitle}>
        {isMobile && (
          <div id="transactionsTitle" className={styles.title}>
            {lang.payoutHistory()}
          </div>
        )}
        {!isLoading && hasPayoutsFiltered && payoutsFromHook.length > maxRusultMob && isMobile && (
          <Button
            className={styles.viewAll}
            variant={ButtonTypesEnum.Link}
            onClick={() => navigate(UI_ROUTES.payoutHistory)}
          >
            {lang.commonViewAll()}
          </Button>
        )}
      </div>

      {!isMobile && (
        <div className={styles.titleViewWrapper}>
          <div id="transactionsTitle" className={styles.title}>
            {lang.payoutHistory()}
          </div>
        </div>
      )}

      {showNeverMadePayout && (
        <div className={styles.payoutsNoDataContent}>
          <img src={NoDataIcon} alt="Empty data" />
          <div className={styles.payoutsNoPayoutsDescription}>{lang.haventMadePayout()}</div>
        </div>
      )}

      {isUserVerified
        && (isLoading || hasPayoutsAllTime)
        && (() => {
          let containerClass = styles.wrapperTableNoFullScreen;
          if (isMobile) {
            containerClass = styles.wrapperTransactionsMobile;
          } else if (browserWidthSize >= XL_BREAKPOINT_WIDTH) {
            containerClass = styles.wrapperTableFullScreen;
          }

          return (
            <div className={containerClass}>
              {isMobile ? (
                <div className={styles.itemWrapper}>
                  {(() => {
                    if (isLoading) {
                      return (
                        <>
                          {skeletonIds.map((id) => (
                            <div key={id} className={styles.skeletonLoaderRow}>
                              <div className={styles.skeletonLoaderRowDetails}>
                                <div className={styles.skeletonLoaderRowIcon} />
                                <div className={styles.skeletonLoaderRowInfo}>
                                  <div className={styles.skeletonLoaderRowAmountType} />
                                  <div className={styles.skeletonLoaderRowDate} />
                                </div>
                              </div>
                              <div className={styles.skeletonLoaderRowAmount} />
                            </div>
                          ))}
                        </>
                      );
                    }
                    if (showDateRangeEmpty) {
                      return (
                        <div className={styles.wrapperNoTransactions}>
                          <img src={DateRangeEmpty} alt="no matches" />
                          <p className={styles.noMessageText}>{lang.noMatchesDateRange()}</p>
                        </div>
                      );
                    }
                    return payoutsFromHook.map((item, index) => {
                      const payment = item.orderOrPayment as Payment;
                      return (
                        <ClickableDiv
                          key={`${payment.payment_reference}`}
                          onClick={() => handlePaymentClick(payment)}
                        >
                          <TransactionItem payment={payment} />
                        </ClickableDiv>
                      );
                    });
                  })()}
                </div>
              ) : (
                <div className={styles.transactionRowsContainer}>
                  <div className={styles.wrapperDateRangePicker}>
                    {hasPayoutsAllTime && (
                      <DateRangePickerWrapper
                        initialState={{
                          startDate: new Date(oldestPayoutISO),
                          endDate: new Date(),
                        }}
                        maxDateCalendar={new Date()}
                        minDateCalendar={new Date(oldestPayoutISO)}
                        onDateSelect={handleDateChange}
                      />
                    )}
                  </div>

                  {(() => {
                    if (isLoading) {
                      return (
                        <>
                          {skeletonIds.map((id) => (
                            <div key={id} className={styles.skeletonLoaderRow}>
                              <div className={styles.skeletonLoaderRowDetails}>
                                <div className={styles.skeletonLoaderRowIcon} />
                                <div className={styles.skeletonLoaderRowInfo}>
                                  <div className={styles.skeletonLoaderRowAmountType} />
                                  <div className={styles.skeletonLoaderRowDate} />
                                </div>
                              </div>
                              <div className={styles.skeletonLoaderRowAmount} />
                            </div>
                          ))}
                        </>
                      );
                    }
                    if (showDateRangeEmpty) {
                      return (
                        <div className={styles.wrapperNoTransactions}>
                          <img src={DateRangeEmpty} alt="no matches" />
                          <p className={styles.noMessageText}>{lang.noMatchesDateRange()}</p>
                        </div>
                      );
                    }
                    return payoutsFromHook.map((item) => {
                      const payment = item.orderOrPayment as Payment;
                      return (
                        <ClickableDiv
                          className={styles.transactionRow}
                          key={`${payment.payment_reference}`}
                          onClick={() => handlePaymentClick(payment)}
                        >
                          <TransactionItem payment={payment} />
                        </ClickableDiv>
                      );
                    });
                  })()}

                  {!isLoading && hasMorePayouts && hasPayoutsFiltered && (
                    <Button
                      className={classNames(styles.btnLoadMore)}
                      variant={ButtonTypesEnum.Link}
                      onClick={() => handleButtonLoadMore()}
                    >
                      {lang.commonLoadMore()}
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })()}

      {isMobile ? (
        <>{showModal && <Modal handleModal={handleModalTransfer} infoModal={infoModal} />}</>
      ) : (
        <BaseModal isOpen={showModal}>
          {showModal && <Modal handleModal={handleModalTransfer} infoModal={infoModal} />}
        </BaseModal>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  allTransactions: state.payment.paymentsAll.data,
  isAllTransactionsLoading: isCallStatusPending(
    PaymentCall.getPaymentsAll,
    state.payment.statusByCall,
  ),
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Transactions);
